import { useMemo, useState } from 'react';
import { Plus, Settings, Trash2, Copy, Loader2, ArrowUp, ArrowDown, ChevronsUp } from 'lucide-react';
import { apiChannelAPI, type ApiChannel, type ChannelHealth, type ChannelHealthWindow } from '../../services/api';
import { getProviderMetadata } from '../../constants/providerMetadata';
import toast from '../Toast';

interface ApiChannelsTabProps {
  channels: ApiChannel[];
  healthMap?: Record<string, ChannelHealth>;
  onRefresh: () => void;
  onEdit: (channel: ApiChannel) => void;
  onCreate: () => void;
  onClone?: (channel: ApiChannel) => void;
}

export default function ApiChannelsTab({ channels, healthMap, onRefresh, onEdit, onCreate, onClone }: ApiChannelsTabProps) {
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [showUsedOnly, setShowUsedOnly] = useState(false);
  const orderedChannels = useMemo(
    () => [...channels].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [channels]
  );
  const hasBoundModels = (channel: ApiChannel) => (channel.models?.length || 0) > 0;
  const visibleChannels = useMemo(
    () => (showUsedOnly ? orderedChannels.filter(hasBoundModels) : orderedChannels),
    [orderedChannels, showUsedOnly]
  );

  const getModelCountBadgeClass = (count: number) =>
    count === 0
      ? 'bg-red-100 text-red-600 border border-red-200'
      : 'bg-light-hover text-light-text border border-light-border';

  const getEmptyChannelHighlightClass = (count: number) =>
    count === 0 ? 'bg-red-50/60' : '';

  const renderCostTags = (channel: ApiChannel, compact = false) => (
    <div className={`flex flex-wrap justify-center gap-1.5 ${compact ? 'sm:justify-end' : ''}`}>
      <span className="inline-flex items-center rounded-full bg-orange-50 px-2.5 py-1 text-xs font-medium text-orange-700 ring-1 ring-orange-100">
        低 ¥{channel.costPerCallLow?.toFixed(4) || '0.0000'}
      </span>
      <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-100">
        中 ¥{channel.costPerCallMedium?.toFixed(4) || '0.0000'}
      </span>
      <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 ring-1 ring-rose-100">
        高 ¥{channel.costPerCallHigh?.toFixed(4) || '0.0000'}
      </span>
    </div>
  );

  // 健康度颜色档：绿(≥95%) / 琥珀(80%-95%) / 红(<80%) / 灰(样本不足或无数据)
  const getHealthBadgeClass = (rate: number | null): string => {
    if (rate === null) return 'bg-gray-100 text-gray-500';
    if (rate >= 95) return 'bg-green-100 text-green-700';
    if (rate >= 80) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  const renderHealthWindow = (window: ChannelHealthWindow | undefined, label: string) => {
    if (!window || window.total === 0) {
      return (
        <span className="px-2 py-0.5 rounded text-[11px] bg-gray-100 text-gray-400">
          {label} 无数据
        </span>
      );
    }
    const rate = window.successRate;
    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getHealthBadgeClass(rate)}`}>
          {rate === null ? '样本不足' : `${rate}%`}
          <span className="ml-1 font-normal opacity-70">{window.total}次</span>
        </span>
        {window.failed > 0 && (
          <span className="text-[10px] text-red-500">失败{window.failed}</span>
        )}
      </div>
    );
  };

  const renderHealthCell = (channelId: string) => {
    const health = healthMap?.[channelId];
    return (
      <div className="flex flex-col items-center gap-1">
        {renderHealthWindow(health?.window1h, '1h')}
        {renderHealthWindow(health?.window24h, '24h')}
      </div>
    );
  };

  // 移动端紧凑健康度：1h/24h 水平排列
  const renderHealthCompact = (channelId: string) => {
    const health = healthMap?.[channelId];
    const windows = [
      { w: health?.window1h, label: '1h' },
      { w: health?.window24h, label: '24h' },
    ];
    return (
      <div className="flex items-center gap-1.5">
        {windows.map(({ w, label }) => {
          if (!w || w.total === 0) {
            return (
              <span key={label} className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-400">
                {label} 无数据
              </span>
            );
          }
          const rate = w.successRate;
          return (
            <span key={label} className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium ${getHealthBadgeClass(rate)}`}>
              {label} {rate === null ? '样本不足' : `${rate}%`}
            </span>
          );
        })}
      </div>
    );
  };

  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除这个 API 渠道吗？')) return;
    try {
      await apiChannelAPI.deleteChannel(id);
      onRefresh();
    } catch (error: any) {
      toast.error(error.message || '删除失败');
    }
  };

  const handleCopyKey = async (id: string) => {
    try {
      setCopyingId(id);
      const { apiKey } = await apiChannelAPI.getChannelKey(id);
      await navigator.clipboard.writeText(apiKey);
      toast.success('API Key 已复制到剪贴板');
    } catch (error: any) {
      toast.error(error.message || '复制失败');
    } finally {
      setCopyingId(null);
    }
  };

  const handleMove = async (channelId: string, direction: 'up' | 'down' | 'top') => {
    const currentIndex = orderedChannels.findIndex((channel) => channel.id === channelId);
    if (currentIndex === -1) return;

    const targetIndex =
      direction === 'top'
        ? 0
        : direction === 'up'
          ? currentIndex - 1
          : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= orderedChannels.length || targetIndex === currentIndex) {
      return;
    }

    const nextChannels = [...orderedChannels];
    const [movedChannel] = nextChannels.splice(currentIndex, 1);
    nextChannels.splice(targetIndex, 0, movedChannel);

    const updates = nextChannels
      .map((channel, index) => ({
        id: channel.id,
        sortOrder: index,
        changed: channel.sortOrder !== index,
      }))
      .filter((item) => item.changed);

    if (updates.length === 0) return;

    try {
      setMovingId(channelId);
      await Promise.all(
        updates.map((item) => apiChannelAPI.updateChannel(item.id, { sortOrder: item.sortOrder }))
      );
      onRefresh();
    } catch (error: any) {
      toast.error(error.message || '排序更新失败');
    } finally {
      setMovingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-light-text">API 渠道管理</h2>
          <p className="text-sm text-light-muted">配置多个 API Key 渠道并绑定到不同模型</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <button
            onClick={() => setShowUsedOnly((current) => !current)}
            className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2 font-medium transition-all duration-200 sm:w-auto ${
              showUsedOnly
                ? 'bg-violet-100 text-violet-700 border border-violet-200'
                : 'bg-white text-light-text border border-light-border hover:bg-light-hover'
            }`}
          >
            {showUsedOnly ? '显示全部渠道' : '仅显示在用渠道'}
          </button>
          <button
            onClick={onCreate}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent-blue px-4 py-2 font-medium text-white transition-all duration-200 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] sm:w-auto"
          >
            <Plus className="w-4 h-4" />
            添加渠道
          </button>
        </div>
      </div>

      {/* Channel List */}
      <div className="bg-white rounded-2xl shadow-lg border border-light-border overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden xl:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-light-hover">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">渠道名称</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">API 类型</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">API URL</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">API model ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">API Key</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">成本/次</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">图生图上限</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">状态</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">健康度</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">绑定模型</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-light-text">备注</th>
                <th className="px-4 py-3 text-center text-sm font-medium text-light-text">操作</th>
              </tr>
            </thead>
            <tbody>
              {visibleChannels.map((channel) => (
                <tr
                  key={channel.id}
                  className={`border-t border-light-border ${getEmptyChannelHighlightClass(channel.models?.length || 0)}`}
                >
                  {(() => {
                    const modelCount = channel.models?.length || 0;
                    const orderedIndex = orderedChannels.findIndex((item) => item.id === channel.id);
                    return (
                      <>
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-light-text">{channel.name}</span>
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const meta = getProviderMetadata(channel.provider);
                      return (
                        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${meta.badgeClassName}`}>
                          {meta.badgeLabel}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-light-hover px-2 py-1 rounded">{channel.apiUrl}</code>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-light-hover px-2 py-1 rounded">{channel.apiModelId || '-'}</code>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <code className="text-sm text-light-muted bg-gray-100 px-2 py-1 rounded">
                        {channel.apiKey || '********'}
                      </code>
                      <button
                        onClick={() => handleCopyKey(channel.id)}
                        disabled={copyingId === channel.id}
                        className="p-1.5 text-accent-blue hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                        title="复制完整 API Key"
                      >
                        {copyingId === channel.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {renderCostTags(channel)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-sm text-light-text">
                      {channel.referenceImageMaxCount ?? '全站兜底'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-col items-center gap-1">
                      <span className={`px-2 py-1 rounded-lg text-xs ${
                        channel.isActive
                          ? 'bg-green-100 text-green-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {channel.isActive ? '启用' : '禁用'}
                      </span>
                      {channel.forceQualityMapping && (
                        <span className="px-2 py-0.5 rounded text-[10px] bg-blue-50 text-blue-600 ring-1 ring-blue-100" title={`强制清晰度: ${{ low: '标准1K', medium: '高清2K', high: '超清4K' }[channel.forcedQuality || ''] || ''}`}>
                          强制{{ low: '1K', medium: '2K', high: '4K' }[channel.forcedQuality || ''] || ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {renderHealthCell(channel.id)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex rounded-full px-2.5 py-1 text-sm font-medium ${getModelCountBadgeClass(modelCount)}`}>
                      {modelCount} 个模型
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {channel.remark ? (
                      <span className="text-sm text-light-muted whitespace-pre-wrap max-w-[200px] block">
                        {channel.remark}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleMove(channel.id, 'top')}
                        disabled={movingId !== null || orderedIndex === 0}
                        className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="置顶"
                      >
                        {movingId === channel.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <ChevronsUp className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleMove(channel.id, 'up')}
                        disabled={movingId !== null || orderedIndex === 0}
                        className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="上移"
                      >
                        <ArrowUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleMove(channel.id, 'down')}
                        disabled={movingId !== null || orderedIndex === orderedChannels.length - 1}
                        className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="下移"
                      >
                        <ArrowDown className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onEdit(channel)}
                        className="p-2 text-accent-blue hover:bg-blue-50 rounded-lg transition-colors"
                        title="编辑"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onClone?.(channel)}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="克隆"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(channel.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="删除"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                      </>
                    );
                  })()}
                </tr>
              ))}
              {visibleChannels.length === 0 && (
                <tr>
                  <td colSpan={12} className="px-4 py-8 text-center text-light-muted">
                    {showUsedOnly ? '暂无在用渠道' : '暂无 API 渠道，点击「添加渠道」创建'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="xl:hidden divide-y divide-light-border">
          {visibleChannels.map((channel) => (
            <div
              key={channel.id}
              className={`p-4 ${channel.models?.length === 0 ? 'bg-red-50/40 border-l-4 border-red-200' : ''}`}
            >
              {(() => {
                const modelCount = channel.models?.length || 0;
                const orderedIndex = orderedChannels.findIndex((item) => item.id === channel.id);
                const meta = getProviderMetadata(channel.provider);
                return (
                  <>
              {/* 顶部：名称 + 状态标签组 */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-light-text truncate">{channel.name}</p>
                  <code className="mt-1 block break-all rounded bg-light-hover px-2 py-1 text-xs text-light-muted">{channel.apiUrl}</code>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${meta.badgeClassName}`}>
                    {meta.badgeLabel}
                  </span>
                  <span className={`px-2 py-0.5 rounded text-xs ${channel.isActive ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}>
                    {channel.isActive ? '启用' : '禁用'}
                  </span>
                </div>
              </div>

              {/* 健康度行（运行态首要信息，独立一行） */}
              <div className="mb-3 flex items-center gap-2 rounded-lg bg-light-hover/50 px-2.5 py-2">
                <span className="text-xs text-light-muted shrink-0">健康度</span>
                {renderHealthCompact(channel.id)}
              </div>

              {/* API model ID + 图生图上限（配置信息，一行两列） */}
              <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
                <div className="flex flex-col gap-0.5">
                  <span className="text-light-muted">API model</span>
                  <code className="break-all rounded bg-light-hover px-1.5 py-0.5 text-light-text">{channel.apiModelId || '-'}</code>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-light-muted">图生图上限</span>
                  <span className="font-medium text-light-text">{channel.referenceImageMaxCount ?? '全站兜底'}</span>
                </div>
              </div>

              {/* 成本标签 */}
              <div className="mb-3">
                <span className="text-xs text-light-muted">成本/次</span>
                <div className="mt-1">{renderCostTags(channel, true)}</div>
              </div>

              {/* API Key */}
              <div className="mb-3 flex min-w-0 items-center gap-2">
                <code className="min-w-0 break-all rounded bg-gray-100 px-2 py-1 text-xs text-light-muted">
                  {channel.apiKey || '********'}
                </code>
                <button
                  onClick={() => handleCopyKey(channel.id)}
                  disabled={copyingId === channel.id}
                  className="p-1.5 text-accent-blue hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 shrink-0"
                  title="复制完整 API Key"
                >
                  {copyingId === channel.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>

              {/* 绑定模型 + 强制清晰度 + 备注 */}
              <div className="mb-3 flex flex-col gap-1.5 text-xs text-light-text">
                <div className="flex items-center gap-2">
                  <span>绑定 <span className={`inline-flex rounded-full px-1.5 py-0.5 font-medium ${getModelCountBadgeClass(modelCount)}`}>{modelCount}</span> 个模型</span>
                  {channel.forceQualityMapping && (
                    <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 ring-1 ring-blue-100">
                      强制{{ low: '1K', medium: '2K', high: '4K' }[channel.forcedQuality || ''] || ''}
                    </span>
                  )}
                </div>
                {channel.remark ? (
                  <span className="text-light-muted whitespace-pre-wrap">备注：{channel.remark}</span>
                ) : (
                  <span className="text-gray-400">备注：-</span>
                )}
              </div>

              {/* 操作按钮：主操作 + 排序 + 危险操作 */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => onEdit(channel)}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-accent-blue hover:opacity-90 rounded-lg transition-opacity"
                >
                  <Settings className="w-4 h-4" />
                  编辑
                </button>
                <button
                  onClick={() => onClone?.(channel)}
                  className="flex items-center gap-1 px-3 py-2 text-sm text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  克隆
                </button>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleMove(channel.id, 'top')}
                    disabled={movingId !== null || orderedIndex === 0}
                    className="p-2 text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="置顶"
                  >
                    {movingId === channel.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronsUp className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleMove(channel.id, 'up')}
                    disabled={movingId !== null || orderedIndex === 0}
                    className="p-2 text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="上移"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleMove(channel.id, 'down')}
                    disabled={movingId !== null || orderedIndex === orderedChannels.length - 1}
                    className="p-2 text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    title="下移"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => handleDelete(channel.id)}
                  className="ml-auto flex items-center gap-1 px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  删除
                </button>
              </div>
                  </>
                );
              })()}
            </div>
          ))}
          {visibleChannels.length === 0 && (
            <div className="px-4 py-8 text-center text-light-muted">
              {showUsedOnly ? '暂无在用渠道' : '暂无 API 渠道，点击「添加渠道」创建'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
